import { ErrorMessage, Field, Form, useFormikContext } from 'formik';
import { CSSProperties, SyntheticEvent, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';

import { useModalReturn } from '@/resources/hooks/useModal';
import { useTextColor } from '@/resources/hooks/useTextColor';
import { ClientTypes } from '@/services/clients';

import { canChangeOrder } from '../../../../services/global';
import { productHasSpecs } from '../../../../services/products';
import { ICartProduct } from '../../../../store/ducks/order/types';
import { IOptionGroup, IProduct } from '../../../../store/ducks/products/types';
import { useProductLink } from '../../../hooks/useProductLink';
import { useWebContext } from '../../../hooks/useWebContext';
import QuantityInput from '../../cart/reviewItemModal/QuantityInput';
import Options from '../../product/options/Options';
import { getScheduleByDay } from './components/formUtils';
import { ScheduleUnavailableButton } from './components/ScheduleUnavailableButton';
import { MedicinePrescriptionModal } from './MedicinePrescriptionModal';

interface Props {
  action: 'add' | 'receive';
  product: IProduct | ICartProduct;
  fullWidth?: boolean;
  formClasses: string;
  styles?: CSSProperties;
  btnStyles?: CSSProperties;
  optionsGroup: IOptionGroup | null;
  currentMeasure: 'kg' | 'unity';
  quantityInputDirection?: 'center' | 'end';
  isAvailable?: boolean;
  optionModal: useModalReturn;
  pbmBuyButton?: boolean;
  hideQuantityInput?: boolean;
}

function BuyForm({
  action,
  product,
  optionsGroup,
  fullWidth,
  styles,
  btnStyles,
  formClasses,
  currentMeasure,
  optionModal,
  quantityInputDirection,
  isAvailable = true,
  pbmBuyButton = false,
  hideQuantityInput = false,
}: Props) {
  const { order, setShowIdenfificationModal } = useWebContext();
  const history = useHistory();
  const link = useProductLink({ product });
  const { isSubmitting, submitForm } = useFormikContext();
  const { setShowIsCartBlockedModal, configs } = useWebContext();
  const textColor = useTextColor(configs.buy_button_color ?? '#208839');
  const blockedMedicine = !!product?.prescription_retention;
  const [buyBtnClicked, setBuyBtnClicked] = useState<boolean>(false);
  const [showIsMedicinePrescriptionModal, setShowIsMedicinePrescriptionModal] =
    useState<boolean>(false);

  const pbmPromotions = product?.alerts.filter((alert) => alert.type.includes('pbm'));
  const hasPbmConditions = pbmPromotions && pbmPromotions.length > 0 && configs.show_pbm_price;
  const shouldShowVerMaisButton = pbmBuyButton && hasPbmConditions;
  const productScheduleAvailable = getScheduleByDay(product?.schedule);

  const handleAddProductToCart = () => {
    if (productHasSpecs(product as IProduct)) {
      setBuyBtnClicked(false);
      history.push(link);
      return;
    }

    const { isValid, showIsCartBlockedModal } = canChangeOrder({
      type: 'item',
      action: 'adicionado',
    });

    if (!isValid || isSubmitting) return;

    if (optionsGroup && optionsGroup.options.length > 0) optionModal.toggle();
    else submitForm();

    if (showIsCartBlockedModal) setShowIsCartBlockedModal(true);
  };

  useEffect(() => {
    if (buyBtnClicked && blockedMedicine) {
      setShowIsMedicinePrescriptionModal(true);
    }
  }, [buyBtnClicked]);

  const disabledButton = !isAvailable;

  const getStyles = () => {
    if (!isAvailable) {
      return { padding: '3px .75rem', ...btnStyles };
    }

    return {
      padding: '3px .75rem',
      backgroundColor: configs.buy_button_color,
      borderColor: configs.buy_button_color,
      color: textColor,
      ...btnStyles,
    };
  };

  return (
    <>
      <Form
        className={formClasses}
        style={{
          position: 'absolute',
          zIndex: 2,
          right: 10,
          bottom: 15,
          padding: '0 10px',
          ...styles,
        }}
      >
        {!productScheduleAvailable.isAvailable && (
          <ScheduleUnavailableButton
            title={product.title}
            productSchedule={productScheduleAvailable}
          />
        )}
        <Field type="hidden" name="product_id" />

        {disabledButton && (
          <>
            {!isAvailable && (
              <>
                <small className="badge badge-danger float-right mb-2">Produto indisponível</small>
                <div className="clearfix"></div>
              </>
            )}
          </>
        )}

        {action === 'add' && productScheduleAvailable.isAvailable ? (
          <button
            type="button"
            disabled={buyBtnClicked || isSubmitting || disabledButton}
            style={!disabledButton ? getStyles() : {}}
            className={`btn ${disabledButton ? 'btn-secondary' : ''} ${
              fullWidth ? 'btn-block' : 'ml-auto d-block'
            } sale-btn`}
            onClick={(e: SyntheticEvent<HTMLButtonElement>) => {
              e.preventDefault();

              if (shouldShowVerMaisButton) {
                history.push(link);
                return;
              }

              setBuyBtnClicked(true);
              if (blockedMedicine) {
                setShowIsMedicinePrescriptionModal(true);
              }
              if (!blockedMedicine) {
                handleAddProductToCart();
              }

              setBuyBtnClicked(false);
            }}
          >
            {isSubmitting && (
              <Spinner
                className="mr-1"
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            )}
            <span className="font-weight-bold">
              {shouldShowVerMaisButton ? 'VER MAIS' : 'COMPRAR'}
            </span>
          </button>
        ) : (
          <>
            {!hideQuantityInput && productScheduleAvailable.isAvailable && (
              <>
                <QuantityInput
                  type="text"
                  name="quantity"
                  automaticSubmit
                  product={product}
                  fullWidth={fullWidth}
                  currentMeasure={currentMeasure}
                  direction={quantityInputDirection}
                  optionModal={optionModal}
                />
                <ErrorMessage name="quantity" />
              </>
            )}
          </>
        )}
      </Form>
      <MedicinePrescriptionModal
        show={showIsMedicinePrescriptionModal}
        addToCart={handleAddProductToCart}
        onHide={() => {
          if (order?.client.type === ClientTypes.ANONYMOUS) {
            optionModal.toggle();
            setShowIdenfificationModal(true);
          }
          setShowIsMedicinePrescriptionModal(false);
        }}
      />
      {optionsGroup && optionsGroup.options.length > 0 && (
        <div
          onClick={(e: SyntheticEvent) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Options
            action={action}
            modal={optionModal}
            product={product}
            options={optionsGroup.options}
          />
        </div>
      )}
    </>
  );
}

export default BuyForm;
